import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  Divider,
  MenuItem,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OptionModal from '../../Modals/OptionModal.tsx';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { LeadFormActions, ConstantActions } from '../../../Services/_actions/index.js';
import HotLeadCriteriaModal from '../HotLeadCriteriaModal.tsx';
import QuickTemplateModal from '../QuickTemplateModal.tsx';
import EntityDropdown from '../../Shared/EntityDropdown/EntityDropdown.tsx';
import FormPreviewModal from './FormPreviewModal.tsx';
import writeXlsxFile from 'write-excel-file';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import GroupModal from '../../Modals/GroupModal.tsx';
import { BackButton } from '../../Shared/BackButton/index.jsx';
import { useRecoilState, useSetRecoilState, useRecoilValue } from 'recoil';
import {
  campaignLeadManagementAtom,
  entityPreviewDataAtom,
  userInformationAtom,
} from '../../../Services/_states/index.js';
import AccessDeniedPage from '../../../authorization/AccessDeniedPage';
import { authzContextAtom } from '../../../authorization/authzState';
import { canCreateWorkspaceTable } from '../../../features/business-functions/utils/canCreateWorkspaceTable';
import LoadingButton from '../../Shared/Loading/index.tsx';
import { EntityActions } from '../../../Services/_actions/Entity/entity.actions.ts';
import CommonBadges from '../../Shared/Badges/index.jsx';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import EntitySettingsModal from './EntitySettingsModal.tsx';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import FieldSettingsProps from './FieldSettings.tsx';
import ModalForms from '../../Shared/Modals/index.js';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Switch from '@mui/material/Switch';
import { staticLeadTableColumns } from '../DynamicFormFields/helper.ts';
import TabularFormView from './TabularFormView.tsx';

const AttributeTypes = [
  { value: 'STRING', label: 'String' },
  { value: 'INT', label: 'Int' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'PASSWORD', label: 'Password' },
  { value: 'PHONE', label: 'Phone' },
  { value: 'RADIO', label: 'Radio' },
  { value: 'DROPDOWN', label: 'Dropdown' },
  { value: 'ENTITY', label: 'Entity' },
  { value: 'CHECKBOX', label: 'Checkbox' },
  { value: 'TEXTAREA', label: 'Textarea' },
  { value: 'BOOLEAN', label: 'Boolean' },
  { value: 'FLOAT', label: 'Float' },
  { value: 'DATE', label: 'Date' },
  { value: 'FILE', label: 'File' },
  { value: 'TIME', label: 'Time' },
  { value: 'CITY', label: 'City' },
  { value: 'PINCODE', label: 'Pincode' },
  { value: 'CUSTOM', label: 'Custom' },
];

const CreateEditLeadForm: React.FC = () => {
  const { campaignId, leadFormId } = useParams();
  const entityActions = EntityActions();
  const setCampaignLeadManagementData = useSetRecoilState(campaignLeadManagementAtom);
  // const [entityPreviewData, setEntityPreviewData] = useRecoilState(entityPreviewDataAtom);
  const actions = LeadFormActions();
  const location = useLocation();
  const { leadFormData } = location.state || {};
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const authz = useRecoilValue(authzContextAtom);
  const userInfo = useRecoilValue(userInformationAtom);
  const { userHasPermission } = ConstantActions();
  const canCreateTable = canCreateWorkspaceTable(authz, {
    workspaceId: campaignId,
    userInfo,
    legacyUserHasPermission: userHasPermission,
  });
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState(false);
  const [errors, setErrors] = useState < { [key: number]: any } > ({});
  const [entityModalIndex, setEntityModalIndex] = useState < number | null > (null);
  const [existingMaxItemId, setExistingMaxItemId] = useState(0);
  const [description, setDescription] = useState('');
  const [useForAi, setUseForAi] = useState(0);
  const [leadsFormItems, setLeadsFormItems] = useState < any[] > ([
    {
      key_name: 'lead_id',
      alias_name: '',
      key_type: 'STRING',
      is_required: true,
      is_primary: true,
      order_id: 1,
      item_id: 1,
      key_options: [],
      statusOption: '',
      // is_status: false,
      // is_sub_status: false,
      status_parent_key: '',
       is_encrypted: false
    },
    ...Object.values(staticLeadTableColumns),
  ]);
  const [groupingOptions, setGroupingOptions] = useState < { id: number; title: string }[] > ([]);
  const [showOptionModal, setShowOptionModal] = useState(false);
  const [attributeOptions, setAttributeOptions] = useState < string[] > ([]);
  const [attributeInfo, setAttributeInfo] = useState < any > ({});
  const [showCriteriaModal, setShowCriteriaModal] = useState(false);
  // const [hotLeadCriteria, setHotLeadCriteria] = useState < any[] > ([]);
  const [viewMode, setViewMode] = useState('cards');
  const [showQuickTemplateModal, setShowQuickTemplateModal] = useState(false);
  const [quickTemplates, setQuickTemplates] = useState < any[] > ([]);
  const [groupTitleError, setGroupTitleError] = useState < string | null > (null); // State to hold error message
  const [showPreview, setShowPreview] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const HEX = /^#[0-9A-Fa-f]{6}$/;
  const [hotLeadCriteria, setHotLeadCriteria] = useState < any[] > ([
    {
      name: 'is_hot_level_1',
      operation: [
        {
          name: 'or',
          items: [],
        },
      ],
    },
  ]);
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [settingData, setSettingData] = useState < any > ({});
  const isStatusField = (f?: any) => f?.statusOption === 'is_status';
  const isSubStatusField = (f?: any) => f?.statusOption === 'is_sub_status';
  const modifyEntityFormFieldsArray = (list: any[]) => {
    if (!list) return [];
    try {
      const currentEntityFormFields =
        list
          ?.filter((item) => !['file', 'video', 'image', 'audio', 'document']?.includes(item?.type)) // keep only valid ones
          .map((item) => ({ name: item?.name, label: item?.label })) ?? [];
      return currentEntityFormFields;
    } catch (error) {
      console.log(error);
      return [];
    }
  };

  const [draggedIndex, setDraggedIndex] = useState < number | null > (null);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (index: number) => {
    if (draggedIndex === null || draggedIndex === index) return;

    const reordered = [...leadsFormItems];
    const [moved] = reordered.splice(draggedIndex, 1);
    reordered.splice(index, 0, moved);

    const updated = reordered.map((item, i) => ({
      ...item,
      order_id: i + 1,
    }));

    setLeadsFormItems(updated);
    setDraggedIndex(null);
  };

  // const getEntityFormById = async (entity) => {
  //   try {
  //     if (!entity?._id) return;
  //     if (entityPreviewData?.[entity?._id]) return;
  //     const response = await entityActions?.getFormById(entity?._id);
  //     if (response?.status === 200 && response?.data?.items) {
  //       const form = response?.data?.items;
  //       setEntityPreviewData({ ...entityPreviewData, [entity?._id]: form });
  //     }
  //   } catch (error) {
  //     console.log(error);
  //   }
  // };

  // const breadcrumbsData = [
  //   {
  //     label: (
  //       <Link to="/r/management/manageCampaign">
  //         <IconButton color="primary">
  //           <HomeIcon />
  //         </IconButton>
  //       </Link>
  //     ),
  //     href: "/r/management/manageCampaign",
  //   },
  //   { label: 'Campaigns', onClick: () => navigate('/r/leads/campaigns') },
  //   { label: 'Forms', onClick: () => navigate(`/r/leads/${campaignId}/form`) },
  //   { label: `${leadFormId ? 'Edit' : "Create"}  Lead Form` },
  // ];

  const updateLocalStorageWebForm = (payload) => {
    try {
      const existingRaw = localStorage.getItem('leadWebFormData');
      let existing: any = {};
      try {
        existing = existingRaw ? JSON.parse(existingRaw) : {};
      } catch {
        existing = {};
      }
      const withId = { ...payload, leads_form_id: leadFormId, campaign_id: campaignId };
      localStorage.setItem('leadWebFormData', JSON.stringify({ ...existing, webForm: withId }));
      setCampaignLeadManagementData((prev) => ({ ...prev, selectedForm: withId }));
    } catch (e) {
      console.error('Failed to update localStorage:', e);
    }
  };

  const exportToExcel = async () => {
    const formItemsArray = Array.isArray(leadsFormItems)
      ? leadsFormItems
      : Object.values(leadsFormItems || {});

    const schema = [
      { column: 'Field ID', type: String, value: (row) => String(row.item_id || '') },
      { column: 'Field Name', type: String, value: (row) => row.key_name || '' },
      { column: 'Alias Name', type: String, value: (row) => row.alias_name || '' },
      { column: 'Field Type', type: String, value: (row) => row.key_type || '' },
      {
        column: 'Dropdown Value',
        type: String,
        value: (row) =>
          ['DROPDOWN', 'CHECKBOX', 'RADIO'].includes(row.key_type)
            ? (row.key_options || []).join(', ')
            : '',
      },
      {
        column: 'Selected Entity',
        type: String,
        value: (row) => (row.key_type === 'ENTITY' && row?.entity?.name ? row.entity.name : ''),
      },
    ];

    await writeXlsxFile(formItemsArray, {
      schema,
      fileName: `${name || 'LeadForm'}.xlsx`,
    });
  };

  const handleAddAttribute = () => {
    setLeadsFormItems((prev) => {
      const newItemId = Math.max(existingMaxItemId, ...prev.map((item) => item.item_id || 0)) + 1;

      return [
        ...prev,
        {
          key_name: '',
          alias_name: '',
          key_type: '',
          is_required: false,
          order_id: prev.length + 1,
          item_id: newItemId,
          key_options: [],
           is_encrypted: false
        },
      ];
    });
  };

  const handleAddplusAttribute = (index: number) => {
    const updated = [...leadsFormItems];
    const newItemId = Math.max(existingMaxItemId, ...updated.map((item) => item.item_id || 0)) + 1;
    updated.splice(index + 1, 0, {
      key_name: '',
      alias_name: '',
      key_type: '',
      is_required: false,
      order_id: index + 2,
      item_id: newItemId,
      key_options: [],
      statusOption: '',
      // is_status: false,
      // is_sub_status: false,
      status_parent_key: '',
      // sub_status_mapping: {},
       is_encrypted: false
    });

    setLeadsFormItems(updated);
  };
  const handleAddFieldMapping = (attrIndex: number) => {
    setLeadsFormItems((prev) => {
      if (!prev[attrIndex]) return prev;

      // Clone the attribute at the given index
      const updatedAttribute = {
        ...prev[attrIndex],
        query_parameter: [
          ...(prev[attrIndex].query_parameter || []),
          { param: '', matched_with: '' },
        ],
      };

      // Clone the array and replace the attribute
      const updated = [...prev];
      updated[attrIndex] = updatedAttribute;

      return updated;
    });
  };

  const handleRemoveAttribute = async (item, fieldIndex: number) => {
    const updated = [...leadsFormItems];
    updated.splice(fieldIndex, 1);
    try {
      if (leadFormId) {
        const res: any = await actions.deleteFormFieldsByItemId(leadFormId, item?.item_id);
        if (res?.status === 200) {
          showSnackBar('Form Field deleted successfully', 'success');
          setLeadsFormItems(updated);
          const leadsFormObject = updated?.reduce((acc, field, index) => {
            const item = { ...field, order_id: index + 1, item_id: index + 1 };
            acc[item.item_id] = item;
            return acc;
          }, {});
          const payload = {
            leads_form_name: name,
            description: description,
            use_for_ai: useForAi,
            leads_form_items: leadsFormObject,
            global_hot_lead_criteria: hotLeadCriteria,
            hotness_mapping: {},
            group_options: groupingOptions,
            templates: quickTemplates || [],
          };
          updateLocalStorageWebForm(payload);
        } else {
          showSnackBar('Form Field is not deleted successfully', 'error');
        }
      } else {
        setLeadsFormItems(updated);
      }
    } catch (err) {
      showSnackBar(err?.toString() || 'Error', 'error');
    }
  };

  const handleCloneAttribute = (index: number) => {
    const newItemId =
      Math.max(existingMaxItemId, ...leadsFormItems?.map((item) => item.item_id || 0)) + 1;
    const cloned = {
      ...leadsFormItems[index],
      order_id: leadsFormItems?.length + 1,
      item_id: newItemId,
    };
    setLeadsFormItems([...leadsFormItems, cloned]);
  };

  const handleAttributeChange = (updatedAttribute: any, index: number) => {
    const isOptionType = ['DROPDOWN', 'CHECKBOX', 'RADIO'].includes(updatedAttribute?.key_type);
    if (!isOptionType) {
      delete updatedAttribute.key_options;
      delete updatedAttribute.option_colors;
      if (updatedAttribute.statusOption) {
        updatedAttribute.statusOption = '';
        updatedAttribute.status_parent_key = '';
        // delete updatedAttribute.sub_status_mapping;
      }
    }

    const updated = [...leadsFormItems];
    updated[index] = updatedAttribute;
    setLeadsFormItems(updated);
  };

  const handleAddGroupTitle = (index: number, title: string) => {
    const trimmedTitle = (title || '').trim();

    if (!trimmedTitle) {
      setGroupTitleError('Group title cannot be empty');
      return;
    }

    const exists = groupingOptions.some((g) => g.title.trim() === trimmedTitle);
    if (exists) {
      setGroupTitleError('Group title already exists');
      return;
    }

    setGroupTitleError(null);

    const newGroup = { id: groupingOptions.length + 1, title: trimmedTitle };
    setGroupingOptions([...groupingOptions, newGroup]);

    const updatedAttribute = {
      ...leadsFormItems[index],
      groupId: newGroup.id,
      group_title: trimmedTitle,
    };

    delete updatedAttribute.group_title;
    handleAttributeChange(updatedAttribute, index);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      let campaignName = '';
      try {
        const leadWebFormData = localStorage.getItem('leadWebFormData');
        if (leadWebFormData) {
          const parsedData = JSON.parse(leadWebFormData);
          campaignName = parsedData?.campaign?.name || '';
        }
      } catch (error) {
        console.error('Error reading campaign name from localStorage:', error);
      }

      let hasError = false;
      const newErrors: { [key: number]: any } = {};
      const errorSummary: string[] = [];
      if (!name.trim()) {
        setNameError(true);
        hasError = true;
        errorSummary.push('Form Name is required');
      } else {
        setNameError(false);
      }
      const keyNamesSet = new Set < string > ();
      const aliasNamesSet = new Set < string > ();

      leadsFormItems.forEach((field, idx) => {
        const fieldErrors: any = {};
        if (!field.key_name?.trim()) {
          fieldErrors.key_name = 'Field Name is required';
          hasError = true;
          errorSummary.push(`Field #${idx + 1}: Field Name is missing`);
        }
        if (!field.alias_name?.trim()) {
          fieldErrors.alias_name = 'Alias Name is required';
          hasError = true;
          errorSummary.push(`Field #${idx + 1}: Alias Name is missing`);
        }
        if (!field.key_type?.trim()) {
          fieldErrors.key_type = 'Field Type is required';
          hasError = true;
          errorSummary.push(`Field #${idx + 1}: Field Type is missing`);
        }
        if (field.key_name && keyNamesSet.has(field.key_name.trim())) {
          fieldErrors.key_name = 'Field Name must be unique';
          hasError = true;
          errorSummary.push(`Field #${idx + 1}: Field Name is duplicated`);
        } else {
          keyNamesSet.add(field.key_name?.trim());
        }

        if (field.alias_name && aliasNamesSet.has(field.alias_name.trim())) {
          fieldErrors.alias_name = 'Alias Name must be unique';
          hasError = true;
          errorSummary.push(`Field #${idx + 1}: Alias Name is duplicated`);
        } else {
          aliasNamesSet.add(field.alias_name?.trim());
        }

        if (field.key_type === 'TEXTAREA') {
          const min = parseInt(field.minLength, 10);
          const max = parseInt(field.maxLength, 10);

          if (isNaN(min) || min < 0) {
            fieldErrors.minLength = 'Min Characters must be a valid non-negative number';
            hasError = true;
            errorSummary.push(`Field #${idx + 1}: Invalid Min Characters`);
          }

          if (isNaN(max) || max <= 0) {
            fieldErrors.maxLength = 'Max Characters must be a valid positive number';
            hasError = true;
            errorSummary.push(`Field #${idx + 1}: Invalid Max Characters`);
          }

          if (!isNaN(min) && !isNaN(max) && max <= min) {
            fieldErrors.maxLength = 'Max Characters must be greater than Min Characters';
            hasError = true;
            errorSummary.push(`Field #${idx + 1}: Max Characters must be greater than Min`);
          }
        }

        if (field.is_hot_lead && !field.hot_lead_criteria?.trim()) {
          fieldErrors.hot_lead_criteria = 'Hot Record Criteria is required';
          hasError = true;
          errorSummary.push(`Field #${idx + 1}: Hot Record Criteria is required`);
        }

        if (field.isGroup) {
          const groupTitle = (field.group_title || '').trim();
          const selectedGroup = field.groupId;

          if (!groupTitle && !selectedGroup) {
            fieldErrors.group_title = 'Group title or selection is required';
            hasError = true;
            errorSummary.push(`Field #${idx + 1}: Group title or selection is missing`);
          }
        }

        if (isStatusField(field) && isSubStatusField(field)) {
          fieldErrors.statusOption = 'A field cannot be both Status and Sub Status';
          hasError = true;
          errorSummary.push(`Field #${idx + 1}: Can't be both Status and Sub Status`);
        }

        const anySubStatus = leadsFormItems.some((f) => isSubStatusField(f));
        const anyStatus = leadsFormItems.some((f) => isStatusField(f));

        if (anySubStatus && !anyStatus) {
          fieldErrors.statusOption = 'Please create at least one Status field first';
          hasError = true;
          errorSummary.push(`Field #${idx + 1}: Sub Status present without any Status fields`);
        }

        if (isSubStatusField(field)) {
          if (!field.status_parent_key?.trim()) {
            fieldErrors.status_parent_key = 'Parent Status Field is required';
            hasError = true;
            errorSummary.push(`Field #${idx + 1}: Missing parent Status`);
          } else {
            const parentIsValid = leadsFormItems.some(
              (f) => isStatusField(f) && f.key_name?.trim() === field.status_parent_key?.trim()
            );
            if (!parentIsValid) {
              fieldErrors.status_parent_key = 'Parent Status is invalid';
              hasError = true;
              errorSummary.push(`Field #${idx + 1}: Invalid parent Status`);
            }
          }
        }

        if (Object.keys(fieldErrors).length > 0) {
          newErrors[idx] = fieldErrors;
        }
      });

      setErrors(newErrors);
      if (hasError) {
        const message =
          errorSummary.length > 0
            ? errorSummary.slice(0, 5).join(', ') + (errorSummary.length > 5 ? '...' : '')
            : 'Form has errors';
        showSnackBar(message, 'error');
        return;
      }

      const sanitized = leadsFormItems.map((field, index) => {
        const { is_status, is_sub_status, ...rest } = field || {};
        return { ...rest, order_id: index + 1, item_id: index + 1 };
      });
      const leadsFormObject = sanitized.reduce(
        (acc, item) => {
          acc[item.item_id] = item;
          return acc;
        },
        {} as Record<number, any>
      );

      const payload = {
        leads_form_name: name,
        campaign_name: campaignName,
        description: description,
        use_for_ai: useForAi,
        leads_form_items: leadFormId ? leadsFormObject : sanitized,
        global_hot_lead_criteria: hotLeadCriteria,
        hotness_mapping: {},
        group_options: groupingOptions,
        templates: quickTemplates || [],
      };
      if (leadFormId) {
        //  saveWebFormToLocal(leadFormId, payload);
        try {
          const res: any = await actions.updateLeadForm(payload, leadFormId);
          if (res?.status === 200) {
            showSnackBar('Form updated successfully', 'success');
            // navigate(`/r/leads/${campaignId}/form`);
            updateLocalStorageWebForm(payload);
            navigate(-1);
          }
        } catch (error) {
          showSnackBar('Failed to update form', 'error');
        }
      } else {
        try {
          const res: any = await actions.createLeadForm(payload, campaignId);
          if (res?.status === 200) {
            showSnackBar('Form created successfully', 'success');
            // navigate(`/r/leads/${campaignId}/form`);
            navigate(-1);
          }
        } catch (error) {
          showSnackBar('Failed to create form', 'error');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenOptionModal = (attrType: string, attribute: any, attrIndex: number) => {
    setShowOptionModal(true);
    setAttributeOptions(attribute.key_options || []);
    setAttributeInfo({ attributeType: attrType, attribute, attrIndex });
  };

  const handleCancelOptionModal = () => {
    setShowOptionModal(false);
    setAttributeOptions([]);
    setAttributeInfo({});
  };

  const handleSubmitOptionModal = (options: string[], info: any) => {
    const cleanedOptions = options.map((opt) => opt.trim()).filter((opt) => opt !== '');
    const uniqueOptions = Array.from(new Set(cleanedOptions));

    if (uniqueOptions.length !== cleanedOptions.length) {
      showSnackBar('Duplicate or empty options are not allowed', 'error');
      return;
    }

    const pickedColors: Record<string, string> = (info?.option_colors ?? {}) as Record<
      string,
      string
    >;
    const prevColors: Record<string, string> = (info?.attribute?.option_colors ?? {}) as Record<
      string,
      string
    >;

    const nextColors: Record<string, string> = {};
    uniqueOptions.forEach((opt) => {
      const fromPicked = pickedColors[opt];
      const fromPrev = prevColors[opt];
      if (fromPicked && HEX.test(fromPicked)) nextColors[opt] = fromPicked;
      else if (fromPrev && HEX.test(fromPrev)) nextColors[opt] = fromPrev;
    });

    setShowOptionModal(false);
    const updated = {
      ...info.attribute,
      key_type: info.attributeType,
      key_options: uniqueOptions,
      option_colors: nextColors, // <-- keep colors here
    };

    // if (updated.statusOption === 'is_sub_status' && updated.status_parent_key) {
    //   updated.sub_status_mapping = {
    //     ...(updated.sub_status_mapping || {}),
    //     [updated.status_parent_key]: uniqueOptions.map((name) => ({ name, triggers: [] })),
    //   };
    // }

    handleAttributeChange(updated, info.attrIndex);
  };

  useEffect(() => {
    if (!leadFormId) {
      console.warn('Missing leadFormId');
      return;
    }

    try {
      let formItems: any = Object.values(leadFormData?.leads_form_items || []);

      const hasLeadId = formItems.some((item: any) => item?.key_name === 'lead_id');
      const hasPhoneNumber = formItems.some((item: any) => item?.key_name === 'phone_number');
      const hasCreatedAt = formItems.some((item: any) => item?.key_name === 'created_at');
      const hasComment = formItems.some((item: any) => item?.key_name === 'last_machadalo_comment.comment');
      const hasAudioSummary = formItems.some((item: any) => item?.key_name === 'audio_summary');
      const hasAiChat = formItems.some((item: any) => item?.key_name === 'aichat');

      if (!hasLeadId) {
        formItems = [
          {
            key_name: 'lead_id',
            alias_name: '',
            key_type: 'STRING',
            is_required: true,
            is_primary: true,
            order_id: 1,
            item_id: 1,
            key_options: [],
          },
          ...formItems.map((item, index) => ({
            ...item,
            order_id: index + 2,
            item_id: index + 2,
          })),
        ];
      }
      if (!hasPhoneNumber) {
        formItems.push(staticLeadTableColumns.phone_number);
      }
      if (!hasCreatedAt) {
        formItems.push(staticLeadTableColumns.created_at);
      }
      if (!hasComment) {
        formItems.push(staticLeadTableColumns['last_machadalo_comment.comment']);
      }
      if (!hasAudioSummary) {
        formItems.push(staticLeadTableColumns.audio_summary);
      }
      if (!hasAiChat) {
        formItems.push(staticLeadTableColumns.aichat);
      }
      if (formItems?.length > 0) {
        const maxId = Math.max(...formItems.map((item) => item.item_id || 0));
        setExistingMaxItemId(maxId);
      }
      formItems = formItems.map((item: any) => {
        const statusOption =
          item?.statusOption ??
          (item?.is_status ? 'is_status' : item?.is_sub_status ? 'is_sub_status' : '');
        const { is_status, is_sub_status, ...rest } = item || {};
        return {
          ...rest,
          groupId: rest?.groupId || '',
          statusOption,
          status_parent_key: rest?.status_parent_key ?? '',
          is_encrypted: rest?.is_encrypted ?? false
        };
      });

      setLeadsFormItems(formItems);
      setName(leadFormData?.leads_form_name || '');
      setDescription(leadFormData?.description || '');
      setUseForAi(leadFormData?.use_for_ai || 0);
      setQuickTemplates(leadFormData?.templates || []);
      setHotLeadCriteria(leadFormData?.global_hot_lead_criteria || []);
      setGroupingOptions(leadFormData?.group_options || []);
      // formItems.forEach((item: any) => {
      //   if (item?.key_type === 'ENTITY' && item?.entity) {
      //     getEntityFormById(item?.entity);
      //   }
      // });
    } catch (error) {
      console.error('Error while setting lead form state or localStorage:', error);
    }
  }, []);
  const renderAttributeRow = (attribute: any, index: number) => (
    <Grid item xs={12} sm={6} md={4} key={`attr-${index}`}>
      <Paper
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          border: draggedIndex === index ? '2px dashed #4F6DF9' : '1px solid transparent',
          backgroundColor: draggedIndex === index ? '#f0f4ff' : 'white',
          cursor: 'grab',
          transition: 'all 0.2s ease',
        }}
        draggable
        onDragStart={() => handleDragStart(index)}
        onDragOver={handleDragOver}
        onDrop={() => handleDrop(index)}
      >
        <Grid container spacing={1} direction="column">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip title="Drag to reorder" arrow>
                <IconButton
                  size="small"
                  sx={{
                    cursor: 'grab',
                    color: draggedIndex === index ? '#4F6DF9' : 'text.secondary',
                  }}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(index)}
                >
                  <DragIndicatorIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Typography variant="subtitle2">Field #{index + 1}</Typography>
            </Box>
            <Box>
              <Tooltip title="Add Permission" arrow>
                <IconButton
                  color="primary"
                  onClick={() => {
                    setPermissionModalOpen(true);
                    setAttributeInfo({ attribute, attrIndex: index });
                    setSettingData(attribute?.settings || {});
                  }}
                  size="small"
                >
                  <SettingsSuggestIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Add New Field on Right Side" arrow>
                <IconButton
                  color="primary"
                  onClick={() => handleAddplusAttribute(index)}
                  size="small"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {!attribute?.is_primary && (
                <>
                  <Tooltip title="Create Duplicate Field" arrow>
                    <IconButton
                      color="primary"
                      onClick={() => handleCloneAttribute(index)}
                      size="small"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete This Field " arrow>
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveAttribute(attribute, index)}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          </Box>

          <Box sx={{ mt: 1 }}>
            {attribute?.is_static_column && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  This is a static column and cannot be edited.
                </Typography>
              </>
            )}
            <TextField
              label={
                <span>
                  Field Name <span style={{ color: 'red' }}>*</span>
                </span>
              }
              disabled={
                attribute?.is_primary || (leadFormId && attribute?.item_id <= existingMaxItemId)
              }
              size="small"
              fullWidth
              value={attribute.key_name === 'lead_id' ? 'record_id' : attribute.key_name}
              // value={attribute.key_name}
              onChange={(e) =>
                handleAttributeChange({ ...attribute, key_name: e.target.value }, index)
              }
              sx={{ mb: 2 }}
              error={!!errors[index]?.key_name}
              helperText={errors[index]?.key_name}
            />
            <TextField
              label={
                <span>
                  Alias Name <span style={{ color: 'red' }}>*</span>
                </span>
              }
              size="small"
              fullWidth
              value={attribute.alias_name}
              onChange={(e) =>
                handleAttributeChange({ ...attribute, alias_name: e.target.value }, index)
              }
              sx={{ mb: 2 }}
              error={!!errors[index]?.alias_name}
              disabled={attribute?.is_static_column ?? false}
              helperText={errors[index]?.alias_name}
            />
            <SearchableDropdown
              label={
                <Typography component="span">
                  Field Type <span style={{ color: 'red' }}>*</span>
                </Typography>
              }
              options={AttributeTypes}
              value={AttributeTypes.find((opt) => opt.value === attribute.key_type) || null}
              onChange={(selected) => {
                const value = selected?.value || '';
                // const isOptionType = ['DROPDOWN', 'CHECKBOX', 'RADIO'].includes(value);
                // if (isOptionType) handleOpenOptionModal(value, attribute, index);
                if (
                  value === 'DROPDOWN' &&
                  attribute.statusOption !== 'is_status' &&
                  attribute.statusOption !== 'is_sub_status'
                ) {
                  handleOpenOptionModal('DROPDOWN', { ...attribute, key_type: value }, index);
                }
                if (value === 'CHECKBOX' || value === 'RADIO') {
                  handleOpenOptionModal(value, attribute, index);
                }
                handleAttributeChange({ ...attribute, key_type: value }, index);
              }}
              labelKey="label"
              valueKey="value"
              // error={!attribute.key_type}
              // errorMessage="Field type is required"
              error={!!errors[index]?.key_type}
              errorMessage={errors[index]?.key_type}
              disabled={attribute?.is_primary}
            />
            {['DROPDOWN', 'CHECKBOX', 'RADIO'].includes(attribute.key_type) && (
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                {(attribute.key_options || []).slice(0, 6).map((opt) => {
                  const bg = attribute.option_colors?.[opt];
                  return (
                    <Box
                      key={opt}
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: 12,
                        lineHeight: 1.4,
                        bgcolor: bg || 'action.hover',
                        color: bg ? '#fff' : 'text.secondary',
                      }}
                    >
                      {opt}
                    </Box>
                  );
                })}
                {(attribute.key_options?.length || 0) > 0 && (
                  <Button
                    size="small"
                    onClick={() => handleOpenOptionModal(attribute.key_type, attribute, index)}
                    sx={{ textTransform: 'none', p: 0 }}
                  >
                    View More
                  </Button>
                )}
                {attribute.key_type === 'DROPDOWN' && (
                  <>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Status Option"
                      value={attribute.statusOption || ''}
                      onChange={(e) => {
                        const val = e.target.value as
                          | 'is_status'
                          | 'is_sub_status'
                          // | 'is_not_status'
                          | '';
                        let updated = { ...attribute, statusOption: val };
                        if (val === 'is_status') {
                          // Be a Status field: clear options/colors; cannot be sub-status
                          updated.status_parent_key = '';
                          updated.key_options = [];
                          updated.option_colors = {};
                          handleAttributeChange(updated, index);
                          return;
                        } else if (val === 'is_sub_status') {
                          // Must have at least one parent Status
                          const parentList = leadsFormItems.filter((f) => isStatusField(f));
                          if (parentList.length === 0) {
                            showSnackBar(
                              'Please create at least one Status field first',
                              'warning'
                            );
                            // revert drop-down visual state
                            updated.statusOption = attribute.statusOption || '';
                            handleAttributeChange(updated, index);
                            return;
                          }

                          if (!updated.status_parent_key) {
                            updated.status_parent_key = parentList[0]?.key_name || '';
                          }

                          // prepare mapping skeleton for current options
                          // const options = Array.isArray(updated.key_options)
                          //   ? updated.key_options
                          //   : [];
                          // updated.sub_status_mapping = {
                          //   ...(updated.sub_status_mapping || {}),
                          //   [updated.status_parent_key]: options.map((opt: string) => ({
                          //     name: opt,
                          //     triggers: [],
                          //   })),
                          // };

                          handleAttributeChange(updated, index);
                          return;
                        }
                        if (!val) {
                          // Plain dropdown (neither status nor sub-status)
                          updated.status_parent_key = '';
                          handleAttributeChange(updated, index);
                          handleOpenOptionModal('DROPDOWN', updated, index);
                          return;
                        }

                        // cleared
                        updated.status_parent_key = '';
                        handleAttributeChange(updated, index);
                      }}
                      sx={{ mt: 1 }}
                    >
                      <MenuItem value="">Default</MenuItem>
                      <MenuItem value="is_status">Status</MenuItem>
                      <MenuItem value="is_sub_status">Sub Status</MenuItem>
                      {/* <MenuItem value="is_not_status">Default</MenuItem> */}
                    </TextField>

                    {/* When sub-status is selected, let the user pick its parent */}
                    {attribute.statusOption === 'is_sub_status' && (
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label="Parent Status Field"
                        value={attribute.status_parent_key || ''}
                        onChange={(e) => {
                          const newParent = e.target.value;
                          // const options = Array.isArray(attribute.key_options) ? attribute.key_options : [];
                          const updated = {
                            ...attribute,
                            status_parent_key: newParent,
                            // sub_status_mapping: {
                            //   ...(attribute.sub_status_mapping || {}),
                            //   [newParent]: options.map((opt: string) => ({ name: opt, triggers: [] })),
                            // },
                          };
                          handleAttributeChange(updated, index);
                        }}
                        sx={{ mt: 1 }}
                        error={!!errors[index]?.status_parent_key}
                        helperText={errors[index]?.status_parent_key}
                      >
                        {leadsFormItems
                          ?.filter((f) => isStatusField(f) && f.order_id < attribute.order_id)
                          ?.map((f) => (
                            <MenuItem key={f.key_name} value={f.key_name}>
                              {f.alias_name || f.key_name}
                            </MenuItem>
                          ))}
                      </TextField>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
          {attribute.key_type === 'TEXTAREA' && (
            <>
              <TextField
                label="Min Characters"
                type="number"
                size="small"
                value={attribute.minLength || ''}
                onChange={(e) =>
                  handleAttributeChange({ ...attribute, minLength: e.target.value }, index)
                }
                fullWidth
                sx={{ mt: 1 }}
                error={!!errors[index]?.minLength}
                helperText={errors[index]?.minLength}
              />
              <TextField
                label="Max Characters"
                type="number"
                size="small"
                value={attribute.maxLength || ''}
                onChange={(e) =>
                  handleAttributeChange({ ...attribute, maxLength: e.target.value }, index)
                }
                fullWidth
                sx={{ mt: 1 }}
                error={!!errors[index]?.maxLength}
                helperText={errors[index]?.maxLength}
              />
            </>
          )}

          {attribute.key_type === 'ENTITY' && (
            <Box sx={{ mt: 1 }}>
              <CommonBadges
                value={attribute?.entity?.name ?? 'Select Entity'}
                variant="outlined"
                color="success"
                icon={
                  <IconButton onClick={() => setEntityModalIndex(index)} size="small">
                    <ModeEditIcon fontSize="small" />
                  </IconButton>
                }
              />
            </Box>
          )}
          <Divider sx={{ my: 1 }} />
          {attribute?.is_primary && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  disabled
                  checked={attribute.is_primary || false}
                  onChange={(e) =>
                    handleAttributeChange({ ...attribute, is_primary: e.target.checked }, index)
                  }
                />
              }
              label="Primary Key"
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                disabled={attribute?.is_primary}
                checked={attribute.is_required || false}
                onChange={(e) =>
                  handleAttributeChange({ ...attribute, is_required: e.target.checked }, index)
                }
              />
            }
            label="Required"
          />
          <FormControlLabel
  control={
    <Switch
      size="small"
      checked={attribute.is_encrypted || false}
      onChange={(e) =>
        handleAttributeChange(
          { ...attribute, is_encrypted: e.target.checked },
          index
        )
      }
    />
  }
  label="Encrypted"
/>
          {!attribute?.is_primary && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={attribute.isGroup || false}
                    onChange={(e) =>
                      handleAttributeChange({ ...attribute, isGroup: e.target.checked }, index)
                    }
                  />
                }
                label="Group"
              />
              {attribute.isGroup && (
                <Box sx={{ pl: 2 }}>
                  <TextField
                    size="small"
                    label="Group Title"
                    value={attribute.group_title || ''}
                    fullWidth
                    onChange={(e) =>
                      handleAttributeChange({ ...attribute, group_title: e.target.value }, index)
                    }
                    sx={{ mt: 1 }}
                    // error={!!groupTitleError}
                    // helperText={groupTitleError}
                    error={!!errors[index]?.group_title || !!groupTitleError}
                    helperText={errors[index]?.group_title || groupTitleError}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleAddGroupTitle(index, attribute.group_title)}
                    sx={{ mt: 2 }}
                  >
                    Add Group
                  </Button>
                  <TextField
                    select
                    label="Select Existing Group"
                    size="small"
                    fullWidth
                    value={attribute.groupId || ''}
                    onChange={(e) =>
                      handleAttributeChange({ ...attribute, groupId: e.target.value }, index)
                    }
                    sx={{ mt: 2, mb: 1 }}
                    error={!!errors[index]?.group_title}
                    helperText={errors[index]?.group_title}
                  >
                    <MenuItem value="">Select Group</MenuItem>
                    {groupingOptions.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.title}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={attribute.isSuggestion || false}
                    onChange={(e) =>
                      handleAttributeChange({ ...attribute, isSuggestion: e.target.checked }, index)
                    }
                  />
                }
                label="Suggestion"
              />
              {attribute.isSuggestion && (
                <TextField
                  size="small"
                  label="Suggestion Message"
                  value={attribute.suggestionMsg || ''}
                  onChange={(e) =>
                    handleAttributeChange({ ...attribute, suggestionMsg: e.target.value }, index)
                  }
                  fullWidth
                  sx={{ mt: 1 }}
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={attribute.is_hot_lead || false}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      let updatedAttribute = {
                        ...attribute,
                        is_hot_lead: isChecked,
                      };
                      if (
                        isChecked &&
                        ['DROPDOWN', 'CHECKBOX', 'RADIO'].includes(attribute.key_type)
                      ) {
                        const optionsString = (attribute.key_options || []).join(', ');
                        updatedAttribute.hot_lead_criteria = optionsString;
                      }

                      handleAttributeChange(updatedAttribute, index);
                    }}
                  />
                }
                label="Hot Record"
              />{' '}
              {attribute.is_hot_lead && (
                <TextField
                  size="small"
                  label="Hot Record Criteria"
                  value={attribute.hot_lead_criteria || ''}
                  onChange={(e) =>
                    handleAttributeChange(
                      { ...attribute, hot_lead_criteria: e.target.value },
                      index
                    )
                  }
                  fullWidth
                  sx={{ mt: 1 }}
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={attribute.isFilter || false}
                    onChange={(e) =>
                      handleAttributeChange({ ...attribute, isFilter: e.target.checked }, index)
                    }
                  />
                }
                label="Is Filter"
              />
              {/* <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={attribute.is_status || false}
                    onChange={(e) => toggleStatus(index, e.target.checked)}
                    disabled={attribute?.is_primary}
                  />
                }
                label="Is Status"
              /> */}
            </>
          )}
        </Grid>
      </Paper>
    </Grid>
  );

  if (!leadFormId && campaignId && !canCreateTable) {
    return <AccessDeniedPage />;
  }

  return (
    <Box sx={{ padding: 2, backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h4" fontWeight="bold" sx={{ py: 2 }}>
            {leadFormId ? 'Edit' : 'Create'} Form
          </Typography>
        </Grid>
        <Button onClick={() => setViewMode(viewMode === 'cards' ? 'table' : 'cards')}>
         {viewMode === 'cards' ? 'Table View' : 'Card View'}
        </Button>
        <Grid item>
          <BackButton onClick={() => navigate(-1)} />
        </Grid>
      </Grid>
      <Box sx={{ pt: 1 }}>
        {/* <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
          <TextField
            label={
              <span>
                Form Name <span style={{ color: 'red' }}>*</span>
              </span>
            }
            size="small"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            error={nameError}
            helperText={nameError ? 'Form Name is required' : ''}
          />
          <TextField
    label="Description"
    size="small"
    name="description"
    value={description}
    onChange={(e) => setDescription(e.target.value)}
    fullWidth
    multiline
    rows={4}
    placeholder="Enter form description..."
    sx={{ mb: 2 }}
  /> 
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
    <Typography variant="body1" sx={{ mr: 2, fontWeight: 500 }}>
      Use for AI
    </Typography>
    <Switch
      checked={useForAi === 1}
      onChange={(e) => setUseForAi(e.target.checked ? 1 : 0)}
    />
    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
      {useForAi === 1 ? 'Enabled' : 'Disabled'}
    </Typography>
  </Box>

          <Tooltip title="Preview Form">
            <IconButton sx={{ ml: 1 }} onClick={() => setShowPreview(true)}>
              <VisibilityIcon color="primary" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download as Excel">
            <IconButton sx={{ ml: 1 }} onClick={exportToExcel}>
              <FileDownloadIcon color="primary" />
            </IconButton>
          </Tooltip>
        </Box> */}
        <Box sx={{ mb: 3, mt: 2 }}>
          {/* Form Name Row with Action Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TextField
              label={
                <span>
                  Form Name <span style={{ color: 'red' }}>*</span>
                </span>
              }
              size="small"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              error={nameError}
              helperText={nameError ? 'Form Name is required' : ''}
            />
            <Tooltip title="Preview Form">
              <IconButton
                sx={{
                  bgcolor: 'primary.light',
                  '&:hover': { bgcolor: 'primary.main', color: 'white' }
                }}
                onClick={() => setShowPreview(true)}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download as Excel">
              <IconButton
                sx={{
                  bgcolor: 'success.light',
                  '&:hover': { bgcolor: 'success.main', color: 'white' }
                }}
                onClick={exportToExcel}
              >
                <FileDownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Description and AI Toggle in Single Row */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Description Field */}
            <TextField
              label="Description"
              size="small"
              name="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Enter form description..."
            />

            {/* Use for AI Toggle - Compact Version */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.5,
                borderRadius: 2,
                border: '1px solid',
                // borderColor: useForAi === 1 ? 'primary.main' : 'grey.300',
                // bgcolor: useForAi === 1 ? 'primary.light' : 'grey.50',
                transition: 'all 0.3s ease',
                minWidth: 180,
              }}
            >
              <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                Use for AI
              </Typography>
              <Switch
                checked={useForAi === 1}
                onChange={(e) => setUseForAi(e.target.checked ? 1 : 0)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'primary.main',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: 'primary.main',
                  },
                }}
              />
              <Typography
                variant="caption"
                fontWeight={500}
              // sx={{ 
              //   color: useForAi === 1 ? 'primary.main' : 'text.secondary',
              //   whiteSpace: 'nowrap',
              // }}
              >
                {useForAi === 1 ? 'Enabled' : 'Disabled'}
              </Typography>
            </Box>
          </Box>
        </Box>
{viewMode === 'table' ? (
  <TabularFormView
    leadsFormItems={leadsFormItems}
    setLeadsFormItems={setLeadsFormItems}
    onAddAttribute={handleAddAttribute}
    onRemoveAttribute={handleRemoveAttribute}
    onCloneAttribute={handleCloneAttribute}
    onAttributeChange={handleAttributeChange}
  />
) : (
 <Grid container spacing={2}>
          {leadsFormItems?.map((attr, idx) => renderAttributeRow(attr, idx))}
        </Grid>
)}
        
        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button variant="contained" size="small" onClick={handleAddAttribute}>
            Add New Fields
          </Button>
          <LoadingButton
            buttonName="Submit"
            size="small"
            onClickButton={handleSubmit}
            isLoading={isSubmitting}
          />
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate(`/r/records/help-file/${leadFormId}`)}
          >
            Add Help File
          </Button>
          <Button variant="contained" size="small" onClick={() => setShowCriteriaModal(true)}>
            Global Hot Record Criteria
          </Button>
          <Button variant="contained" size="small" onClick={() => setShowQuickTemplateModal(true)}>
            Add Quick Template
          </Button>
          {leadFormId && (
            <>
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate(`/r/records/status-list/${campaignId}/${leadFormId}`)}
              >
                Add Status & Sub Status
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate(`/r/records/schedular/${campaignId}/${leadFormId}`)}
              >
                Scheduler
              </Button>
            </>
          )}
          <Button variant="contained" size="small" onClick={() => setShowGroupModal(true)}>
            Group
          </Button>
        </Box>
      </Box>

      <GroupModal
        showOptionModal={showGroupModal}
        onCancel={() => setShowGroupModal(false)}
        onSubmit={(updatedGroupTitles, info) => {
          const removedGroupTitles = (info?.removedGroups || []).map((title) =>
            typeof title === 'string' ? title.trim() : ''
          );

          const updatedGroupOptions = groupingOptions.filter((group) => {
            const title = typeof group?.title === 'string' ? group.title.trim() : '';
            return title && !removedGroupTitles.includes(title);
          });

          updatedGroupTitles.forEach((title) => {
            const cleanedTitle = typeof title === 'string' ? title.trim() : '';
            if (!cleanedTitle) return;

            const alreadyExists = updatedGroupOptions.some(
              (group) =>
                (typeof group?.title === 'string' ? group.title.trim() : '') === cleanedTitle
            );

            if (!alreadyExists) {
              updatedGroupOptions.push({
                id: updatedGroupOptions.length + 1,
                title: cleanedTitle,
              });
            }
          });

          const updatedFormItems = leadsFormItems.map((item) => {
            const groupTitle = typeof item?.group_title === 'string' ? item.group_title.trim() : '';

            if (
              !groupTitle ||
              removedGroupTitles.includes(groupTitle) ||
              !updatedGroupTitles.map((t) => t?.trim?.()).includes(groupTitle)
            ) {
              return {
                ...item,
                groupId: '',
                group_title: '',
                isGroup: false,
              };
            }

            return item;
          });

          setGroupingOptions(updatedGroupOptions);
          setLeadsFormItems(updatedFormItems);
          setShowGroupModal(false);
        }}
        group={groupingOptions.map((g) => g.title)}
        columnInfo={{}}
      />

      <OptionModal
        showOptionModal={showOptionModal}
        onCancel={handleCancelOptionModal}
        onSubmit={handleSubmitOptionModal}
        options={attributeOptions}
        columnInfo={attributeInfo}
      />

      <HotLeadCriteriaModal
        open={showCriteriaModal}
        onClose={() => setShowCriteriaModal(false)}
        onSave={(criteria) => {
          setHotLeadCriteria(criteria);
          setShowCriteriaModal(false);
        }}
        hotLeadFields={leadsFormItems?.filter((f) => f.is_hot_lead)}
        dropdownOptions={leadsFormItems
          .filter((f) => ['DROPDOWN', 'CHECKBOX', 'RADIO'].includes(f.key_type))
          .map((f) => ({
            key_name: f.key_name,
            key_options: f.key_options || [],
          }))}
      />
      <QuickTemplateModal
        open={showQuickTemplateModal}
        onClose={() => setShowQuickTemplateModal(false)}
        quickTemplates={quickTemplates}
        setQuickTemplates={setQuickTemplates}
        onSubmit={(selectedTemplates) => {
          setQuickTemplates(selectedTemplates);
          showSnackBar(`${selectedTemplates.length} template(s) added`, 'success');
        }}
      />
      <FormPreviewModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        leadsFormItems={leadsFormItems}
      />
      <EntitySettingsModal
        open={entityModalIndex !== null}
        onClose={() => setEntityModalIndex(null)}
        onSave={() => setEntityModalIndex(null)}
        attribute={entityModalIndex !== null ? leadsFormItems[entityModalIndex] : {}}
        index={entityModalIndex ?? 0}
        errors={errors}
        leadsFormItems={leadsFormItems}
        // entityPreviewData={entityPreviewData}
        // setEntityPreviewData={setEntityPreviewData}
        handleAttributeChange={handleAttributeChange}
        handleAddFieldMapping={handleAddFieldMapping}
        modifyEntityFormFieldsArray={modifyEntityFormFieldsArray}
      />
      <ModalForms
        show={permissionModalOpen}
        handleClose={() => {
          setPermissionModalOpen(false);
          setAttributeInfo({ attribute: null, attrIndex: -1 });
        }}
        modalHeader="Permission Settings"
        modalBody={
          <FieldSettingsProps
            settingData={settingData || {}}
            setSettingData={setSettingData}
            campaignId={campaignId}
          />
        }
        modalFooter={
          <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={() => setPermissionModalOpen(false)} size="small">
              Close
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (!attributeInfo?.attribute || !attributeInfo?.attrIndex) return;
                handleAttributeChange(
                  { ...attributeInfo?.attribute, settings: settingData ?? {} },
                  attributeInfo?.attrIndex
                );
                setPermissionModalOpen(false);
              }}
              size="small"
            >
              Save
            </Button>
          </Box>
        }
      />
    </Box>
  );
};

export default CreateEditLeadForm;
